// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import instadate from 'instadate';
import { formActions, mergeActionsToProps } from 'redux-pure-form';
import Datepicker from '../../common/datepicker/datepicker';
import Select from '../../form/select.js';
import { selectStartTimeOptions, selectFinishTimeOptions } from '../calendar.selectors.js';
import {
  capitalize,
  newDate,
  getMinutes,
  parseTime,
  parseTimeInput,
  formatDate,
  applyTimezone,
  getParamsForEventAction,
  paramsToHash,
  getTimestamp,
  getBrowserOffset
} from '../calendar.model.js';
import { dateFormatSettings, timeFormatSettings } from '../../settings/settings.consts.js';
import { checkEventParams } from '../calendar-day.actions.js';
import { dayHalf } from '../calendar.consts.js';

class DateSelect extends React.Component<Props, State> {
  constructor(props) {
    super(props);
    this.timer = null;

    let minutes = getMinutes(
      props.eventForm[`${props.period}Time`],
      props.eventForm.timezone.offset,
      true
    );
    this.state = {
      timeInputLabel: parseTime(minutes, props.user.settings.time),
      timeInputValue: minutes
    };

    this.handleDateChange = this.handleDateChange.bind(this);
    this.handleTimeChange = this.handleTimeChange.bind(this);
  }

  timer: ?number;

  componentWillReceiveProps(nextProps) {
    if (
      (!this.props.eventForm.timezone.offset &&
        nextProps.eventForm.timezone &&
        nextProps.eventForm.timezone.offset) ||
      this.props.eventForm.timezone.offset !== nextProps.eventForm.timezone.offset
    ) {
      const minutes = getMinutes(
        this.props.eventForm[`${this.props.period}Time`],
        nextProps.eventForm.timezone.offset,
        true
      );
      this.setState({
        timeInputLabel: parseTime(minutes, this.props.user.settings.time),
        timeInputValue: minutes
      });
    }
    if (
      (this.props.period === 'finish' &&
        nextProps.eventForm.finishTime !== this.props.eventForm.finishTime) ||
      (this.props.period === 'start' &&
        nextProps.eventForm.startTime !== this.props.eventForm.startTime)
    ) {
      const minutes = getMinutes(
        nextProps.eventForm[`${this.props.period}Time`],
        this.props.eventForm.timezone.offset,
        true
      );
      this.setState({
        timeInputLabel: parseTime(minutes, this.props.user.settings.time),
        timeInputValue: minutes
      });
    }
  }

  componentWillUnmount() {
    if (this.timer) window.clearTimeout(this.timer);
  }

  handleTimeChange(e) {
    this.setState({ timeInputLabel: e.currentTarget.value });
    if (this.timer) window.clearTimeout(this.timer);
    this.timer = window.setTimeout(
      this.handleDateChange.bind(this, { ...e, ...e.currentTarget, nodeName: 'INPUT' }),
      3000
    );
  }

  handleDateChange(val) {
    const sourceEl =
      val && val.currentTarget
        ? val.currentTarget.nodeName === 'INPUT'
          ? 'input'
          : 'select'
        : 'picker';

    const value =
      val && typeof val === 'object' && val.currentTarget ? val.currentTarget.value : val;

    const prevMidnightDate =
      (instadate
        .noon(
          newDate(
            this.props.eventForm[`${this.props.period}Time`],
            this.props.eventForm.timezone.offset,
            true
          )
        )
        .getTime() -
        dayHalf) /
      1000;

    let nextTime;

    switch (sourceEl) {
      case 'input':
        nextTime = prevMidnightDate + parseTimeInput(value);
        break;
      case 'select':
        nextTime = prevMidnightDate + parseInt(value, 10) * 60;
        break;
      case 'picker':
        nextTime =
          (instadate.noon(newDate(getTimestamp(value), 0, false)).getTime() +
            parseInt(this.state.timeInputValue, 10) * 60 * 1000 -
            dayHalf) /
          1000;
        break;
    }

    const normNextTime =
      applyTimezone(nextTime, this.props.eventForm.timezone.offset, true) -
      getBrowserOffset(nextTime);

    const defaultFinishTime = Math.max(
      normNextTime + parseInt(this.props.user.eventDefaultLength, 10),
      normNextTime +
        Math.abs(
          parseInt(this.props.eventForm.finishTime, 10) -
            parseInt(this.props.eventForm.startTime, 10)
        )
    );

    const defaultStartTime = normNextTime - parseInt(this.props.user.eventDefaultLength, 10);

    this.props.fieldAttrs.onChange(`eventForm.${this.props.period}Time`, normNextTime); // in seconds
    this.reset = true;

    let params = getParamsForEventAction(this.props.URL.eventParams.action, {
      ...this.props.eventForm,
      [`${this.props.period}Time`]: normNextTime
    });

    if (
      (this.props.period === 'start' &&
        parseInt(this.props.eventForm.finishTime, 10) !== defaultFinishTime) ||
      (this.props.period === 'finish' &&
        parseInt(this.props.eventForm.startTime, 10) > normNextTime)
    ) {
      const contrPeriod = this.props.period === 'start' ? 'finish' : 'start';
      const nextDefaultTime = contrPeriod === 'start' ? defaultStartTime : defaultFinishTime;

      this.props.fieldAttrs.onChange(`eventForm.${contrPeriod}Time`, nextDefaultTime);

      params = {
        ...params,
        [`${contrPeriod}Time`]: nextDefaultTime
      };
    }

    this.props.paramsToHash(params, this.props.scrollY);
    return this.props.checkEventParams();
  }

  render() {
    const {user, eventForm, timezone, period} = this.props;

    if (!eventForm[`${period}Time`]) return null;

    const options = this.props[`${period}TimeOptions`];
    const label = period === 'start' ? `${capitalize(period)} Time` : `${capitalize('end')} Time`;

    return (
      <div className="form-group">
        <label htmlFor={period} className="required-label">
          {label}
        </label>
        <div className="row">
          <div className="col-xs-7">
            <Datepicker
              id={period}
              className="form-control inner-datepicker input-sm"
              onChange={this.handleDateChange}
              selected={newDate(eventForm[`${period}Time`], eventForm.timezone.offset)}
              dateFormat={user.settings.date}
              required
            />
          </div>

          <div className="col-xs-5 nolp">
            <div className="input-group">
              <input
                className="form-control"
                value={this.state.timeInputLabel}
                onChange={this.handleTimeChange}
              />
              <label className="input-group-btn input-group-time-list">
                <Select
                  className="form-control"
                  options={options}
                  value={this.state.timeInputValue || 0}
                  onChange={this.handleDateChange}
                />
              </label>
            </div>
          </div>
        </div>

        <span className="help-block clean text-muted">
          {formatDate(
            eventForm[`${period}Time`],
            timezone.offset,
            dateFormatSettings[user.settings.date],
            false
          )}{' '}
          {formatDate(
            eventForm[`${period}Time`],
            timezone.offset,
            timeFormatSettings[user.settings.time],
            false
          )}{' '}
          [{timezone.abbr} {timezone.name} {timezone.offsetString}]
        </span>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  UI: state.UI,
  URL: state.URL,
  user: state.user,
  timezone: state.timezone,
  eventForm: state.eventForm,
  scrollY: state.userUI.scrollY,
  startTimeOptions: selectStartTimeOptions(state),
  finishTimeOptions: selectFinishTimeOptions(state)
});

export default connect(
  mapStateToProps,
  {
    checkEventParams,
    paramsToHash,
    ...formActions
  },
  mergeActionsToProps
)(DateSelect);
